/**
 * Per-user order of the patient page tabs (drag and drop).
 *
 * The saved order is a list of tab keys. It only ever reorders tabs: which tabs are shown is
 * still decided by permissions, relation status and modules on the page.
 */

/**
 * Full order of all known tabs: saved keys first (unknown/removed keys and duplicates dropped),
 * then each known tab missing from the saved order inserted at its default position, i.e.
 * right after the nearest tab that precedes it in the default order (or first if none does).
 *
 * The last default tab (Rapporter) is the exception. Until the user has placed it, it stays
 * last, so a moved neighbour such as Aktivitet does not pull it forward. Reset clears the
 * saved order, and the default order applies as-is.
 */
export function mergePatientTabOrder<T extends string>(
  defaultOrder: readonly T[],
  saved: readonly string[] | null | undefined,
): T[] {
  if (!saved || saved.length === 0) return [...defaultOrder];
  const known = new Set<string>(defaultOrder);
  const placed = new Set<string>();
  const result: T[] = [];
  for (const key of saved) {
    if (known.has(key) && !result.includes(key as T)) {
      result.push(key as T);
      placed.add(key);
    }
  }
  defaultOrder.forEach((tab, index) => {
    if (result.includes(tab)) return;
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i--) {
      const pos = result.indexOf(defaultOrder[i]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    result.splice(insertAt, 0, tab);
  });
  const tail = defaultOrder[defaultOrder.length - 1];
  if (tail !== undefined && !placed.has(tail)) {
    const from = result.indexOf(tail);
    if (from !== -1 && from !== result.length - 1) {
      result.splice(from, 1);
      result.push(tail);
    }
  }
  return result;
}

/** The visible tabs in the user's order. */
export function orderVisiblePatientTabs<T extends string>(
  defaultOrder: readonly T[],
  saved: readonly string[] | null | undefined,
  visible: ReadonlySet<T> | readonly T[],
): T[] {
  const isVisible = (tab: T) =>
    Array.isArray(visible) ? (visible as readonly T[]).includes(tab) : (visible as ReadonlySet<T>).has(tab);
  return mergePatientTabOrder(defaultOrder, saved).filter(isVisible);
}

/**
 * New full order after the visible tabs were reordered: hidden tabs keep their slots, the
 * slots of the visible tabs are filled with `visibleOrder` in sequence.
 */
export function applyVisibleTabReorder<T extends string>(
  defaultOrder: readonly T[],
  saved: readonly string[] | null | undefined,
  visibleOrder: readonly T[],
): T[] {
  const full = mergePatientTabOrder(defaultOrder, saved);
  const visible = new Set(visibleOrder);
  let next = 0;
  return full.map((tab) => (visible.has(tab) ? visibleOrder[next++] : tab));
}

/** Moves `active` to the index of `over` within `order` (dnd-kit arrayMove semantics). */
export function moveTab<T extends string>(order: readonly T[], active: T, over: T): T[] {
  const from = order.indexOf(active);
  const to = order.indexOf(over);
  if (from === -1 || to === -1 || from === to) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, active);
  return next;
}

/** Saved order equal to the default is stored as "no custom order". */
export function isDefaultPatientTabOrder<T extends string>(
  defaultOrder: readonly T[],
  order: readonly string[] | null | undefined,
): boolean {
  if (!order || order.length === 0) return true;
  const merged = mergePatientTabOrder(defaultOrder, order);
  return merged.every((tab, i) => tab === defaultOrder[i]);
}

/**
 * The tab that can never be hidden: the first permitted tab in the default order
 * (Aftaler for patients, Relationer for related persons). Keeps at least one tab visible.
 */
export function lockedPatientTab<T extends string>(
  defaultOrder: readonly T[],
  permitted: readonly T[],
): T | null {
  return defaultOrder.find((tab) => permitted.includes(tab)) ?? null;
}

/**
 * Tabs shown in the bar, in the user's order: permitted tabs that the user has not hidden.
 * The locked tab is always shown; `activeTab` is shown even when hidden (a `?tab=` deep link
 * to a hidden tab opens it temporarily) and reported as `temporary`.
 */
export function shownPatientTabs<T extends string>(params: {
  defaultOrder: readonly T[];
  savedOrder: readonly string[] | null | undefined;
  permitted: readonly T[];
  hidden: readonly string[];
  activeTab?: string | null;
}): { tabs: T[]; locked: T | null; temporary: T | null } {
  const { defaultOrder, savedOrder, permitted, hidden, activeTab } = params;
  const locked = lockedPatientTab(defaultOrder, permitted);
  const hiddenSet = new Set(hidden);
  let temporary: T | null = null;
  const tabs = orderVisiblePatientTabs(defaultOrder, savedOrder, permitted).filter((tab) => {
    if (tab === locked || !hiddenSet.has(tab)) return true;
    if (tab === activeTab) {
      temporary = tab;
      return true;
    }
    return false;
  });
  return { tabs, locked, temporary };
}

/** New hidden list after a checkbox change; the locked tab is never hidden, unknown keys dropped. */
export function setPatientTabHidden<T extends string>(
  defaultOrder: readonly T[],
  hidden: readonly string[],
  tab: T,
  hide: boolean,
  locked: T | null,
): T[] {
  const known = new Set<string>(defaultOrder);
  const next = new Set(hidden.filter((key): key is T => known.has(key)));
  if (hide && tab !== locked) next.add(tab);
  else next.delete(tab);
  if (locked) next.delete(locked);
  return defaultOrder.filter((key) => next.has(key));
}
